<script setup lang="ts">
// ================================
// IMPORTS
// ================================
import { computed, onBeforeMount, onMounted, ref } from "vue"
import { useRoute } from "vue-router"
import { useI18n } from "vue-i18n"
import { CONSENT_REQUEST_STATUS, SOURCES_TYPES } from "@core/constants"
import { useAuthStore } from "@core/stores/auth.store"
import { coreServiceRegistry } from "@core/services/register"
import { formatAddress, formatIdSource } from "@core/helpers"
import type { ConsentRequestInvitationDto } from "@types"
import type { Address } from "@types"
import type { OwnerOutput } from "@core/composables"
import type {
    SourceConsentSignBuilding,
    SourceConsentSignSource,
} from "@core/types/source/consent_request"

// ================================
// ROUTE & STORES
// ================================

const route = useRoute()
const authStore = useAuthStore()

// ================================
// I18N
// ================================

const { t } = useI18n({
    messages: {
        fr: {
            /** Aligné sur l’en-tête de `PdlFormOnInvitation` (parcours signature invitation). */
            page_title: "Prolongement du consentement",
            /** Aligné sur `PdlFormOnInvitation` (`success_title`) — ici prolongement accepté. */
            success_title: "Consentement prolongé",
            /**
             * `locationTail` = « situé au … » ou chaîne vide avant le point final
             * (même logique que `PdlFormOnInvitation.success_message`).
             */
            success_message_pdl:
                "{name} vous venez d’accepter le prolongement du consentement pour autoriser l’accès aux données du compteur d’électricité numéro {idsource}{locationTail}. Vous pouvez quitter cette page.",
            success_message_pce:
                "{name} vous venez d’accepter le prolongement du consentement pour autoriser l’accès aux données du compteur de gaz numéro {idsource}{locationTail}. Vous pouvez quitter cette page.",
            alert: "Alerte",
            loading: "Récupération des informations, veuillez patienter...",
            loading_refused: "Mise à jour de la demande, veuillez patienter...",
            expired:
                "Le lien a expiré car la demande est terminée ou bien elle n'existe plus.",
            refused:
                "La demande a bien été refusée. Vous pouvez quitter cette page.",
        },
    },
})

// ================================
// STATE
// ================================

type PagePhase = "loading" | "loading_refused" | "valid" | "error" | "refused"

const phase = ref<PagePhase>("loading")
const invitationRequest = ref<ConsentRequestInvitationDto | null>(null)
/** Après signature API : masque `SourceConsentSign` et affiche le message de succès (comme `PdlFormOnInvitation`). */
const consentSigned = ref(false)

// ================================
// COMPUTED
// ================================

const requestId = computed(() => String(route.params.id ?? ""))

const loadingMessage = computed(() =>
    phase.value === "loading_refused" ? t("loading_refused") : t("loading")
)

/**
 * Adresse postale pour le résumé (priorité corps / bâtiment API).
 */
function getInvitationAddress(data: ConsentRequestInvitationDto): Address {
    if (data.address) {
        return {
            street: data.address.street ?? "",
            zipcode: data.address.zipcode ?? "",
            city: data.address.city ?? "",
            country: data.address.country ?? "FR",
            citycode: data.address.citycode ?? undefined,
        }
    }
    const ad = data.building?.adress
    if (ad) {
        return {
            street: ad.street ?? "",
            zipcode: ad.zipcode ?? "",
            city: ad.city ?? "",
            country: "FR",
            citycode: undefined,
        }
    }
    return {
        street: "",
        zipcode: "",
        city: "",
        country: "FR",
        citycode: undefined,
    }
}

/**
 * Props dérivées de la demande EXTEND pour `SourceConsentSign`.
 */
const getSignProps = computed(() => {
    const data = invitationRequest.value
    if (!data) return null

    const userTo = data.user_to
    const owner: OwnerOutput = {
        user: {
            _id: userTo?._id ?? undefined,
            firstname: userTo?.firstname ?? "",
            lastname: userTo?.lastname ?? "",
            username: userTo?.username ?? "",
        },
        company: userTo?.company?.name
            ? {
                  _id: userTo.company._id,
                  name: userTo.company.name,
                  address: null,
                  type: "",
              }
            : null,
    }

    const src = data.source
    const source: SourceConsentSignSource = {
        _id_source: src?._id,
        idsource: src?.idsource ?? "",
        idtype: src?.idtype ?? "pdl",
        date_from_data: data.consentement?.date_from_data ?? undefined,
    }

    const building: SourceConsentSignBuilding = {
        _id_building: data.building?._id ?? "",
        address: getInvitationAddress(data),
    }

    const duration = data.user_from?.company?.operation?.consent_duration ?? 60

    const dateFromRaw = data.consentement?.date_from
    const dateFrom =
        typeof dateFromRaw === "string" && dateFromRaw.length >= 10
            ? dateFromRaw.slice(0, 10)
            : new Date().toISOString().slice(0, 10)

    return {
        owner,
        source,
        building,
        dateFrom,
        request: {
            _id_consentrequest: data._id,
            type: "EXTEND" as const,
            mail: false,
            duration,
        },
    }
})

const signerName = computed(() => {
    const u = invitationRequest.value?.user_to
    return `${u?.firstname ?? ""} ${u?.lastname ?? ""}`.trim()
})

const formattedIdsource = computed(() => {
    const src = invitationRequest.value?.source
    if (!src?.idsource) return ""
    return formatIdSource(src.idsource, src.idtype ?? "pdl")
})

const getAddressText = computed(() => {
    const data = invitationRequest.value
    if (!data) return ""
    const addr = getInvitationAddress(data)
    if (!addr.street?.trim() && !addr.zipcode?.trim() && !addr.city?.trim())
        return ""
    return formatAddress(addr)
})

const successLocationTail = computed(() =>
    getAddressText.value ? ` situé au ${getAddressText.value}` : ""
)

const successMessage = computed(() => {
    const idtype = invitationRequest.value?.source?.idtype ?? "pdl"
    const key =
        idtype === SOURCES_TYPES.PCE
            ? "success_message_pce"
            : "success_message_pdl"
    return t(key, {
        name: signerName.value,
        idsource: formattedIdsource.value,
        locationTail: successLocationTail.value,
    })
})

// ================================
// LIFECYCLE
// ================================

onBeforeMount(() => {
    authStore.logout()
})

onMounted(() => {
    void runFlow()
})

// ================================
// FLOW
// ================================

async function runFlow(): Promise<void> {
    phase.value = "loading"
    const id = requestId.value
    const token =
        typeof route.query.token === "string" ? route.query.token : undefined
    const action =
        typeof route.query.action === "string" ? route.query.action : undefined

    try {
        const data =
            await coreServiceRegistry.consentRequest.getConsentRequestExtend(
                id,
                token
            )

        if (data.token) {
            authStore.setToken(data.token)
            await authStore.fetchUser()
        }

        invitationRequest.value = data
        const st = data.state

        if (st === CONSENT_REQUEST_STATUS.CREATED && action === "refused") {
            phase.value = "loading_refused"
            await coreServiceRegistry.consentRequest.updateConsentRequestExtend(
                id,
                CONSENT_REQUEST_STATUS.REFUSED,
                { mail: false }
            )
            invitationRequest.value = {
                ...data,
                state: CONSENT_REQUEST_STATUS.REFUSED,
            }
            phase.value = "refused"
            return
        }

        if (st === CONSENT_REQUEST_STATUS.CREATED) {
            phase.value = "valid"
            return
        }

        if (st === CONSENT_REQUEST_STATUS.REFUSED) {
            phase.value = "refused"
            return
        }

        phase.value = "error"
    } catch {
        phase.value = "error"
    }
}

/**
 * Après prolongement réussi : JWT éventuel, puis écran de succès à la place du formulaire.
 */
async function onSigned(payload: { token?: string }): Promise<void> {
    if (payload.token) {
        authStore.setToken(payload.token)
        await authStore.fetchUser()
    }
    consentSigned.value = true
}
</script>

<template>
    <div
        class="page-consent-extend-invitation flex min-h-screen w-full flex-col"
    >
        <ConsentRequestScopeHeader />
        <div class="flex flex-1 justify-center items-start px-4 py-8">
            <div class="w-full max-w-3xl flex flex-col items-center gap-6">
                <template v-if="phase !== 'valid'">
                    <div
                        v-if="
                            phase === 'loading' || phase === 'loading_refused'
                        "
                        class="flex flex-col items-center gap-4 py-12"
                    >
                        <BaseProgressSpinner
                            style="width: 2rem; height: 2rem"
                        />
                        <p class="text-sm text-gray-600 text-center">
                            {{ loadingMessage }}
                        </p>
                    </div>
                    <div
                        v-else-if="phase === 'error'"
                        class="flex flex-col items-center gap-3 text-center max-w-md"
                    >
                        <h1
                            class="text-lg font-semibold text-gray-800 flex items-center gap-2"
                        >
                            {{ t("alert") }}
                            <i
                                class="pi pi-info-circle text-gray-500"
                                aria-hidden="true"
                            />
                        </h1>
                        <p class="text-sm text-amber-800">
                            {{ t("expired") }}
                        </p>
                    </div>
                    <div
                        v-else-if="phase === 'refused'"
                        class="flex flex-col items-center gap-3 text-center max-w-md"
                    >
                        <h1
                            class="text-lg font-semibold text-gray-800 flex items-center gap-2"
                        >
                            {{ t("alert") }}
                            <i
                                class="pi pi-info-circle text-gray-500"
                                aria-hidden="true"
                            />
                        </h1>
                        <p class="text-sm text-gray-700">
                            {{ t("refused") }}
                        </p>
                    </div>
                </template>

                <div
                    v-else-if="getSignProps"
                    class="flex flex-col items-center gap-8 w-full max-w-3xl mx-auto"
                >
                    <header
                        class="flex flex-col items-center gap-8 w-full pt-6"
                    >
                        <h1
                            class="text-xl font-semibold text-gray-800 text-center"
                        >
                            {{
                                consentSigned
                                    ? t("success_title")
                                    : t("page_title")
                            }}
                        </h1>
                        <p
                            v-if="consentSigned"
                            class="text-sm text-gray-700 text-center max-w-2xl"
                        >
                            {{ successMessage }}
                        </p>
                    </header>
                    <SourceConsentSign
                        v-if="!consentSigned"
                        class="w-full"
                        v-bind="getSignProps"
                        hide-header-title
                        hide-back-button
                        @signed="onSigned"
                    />
                </div>
            </div>
        </div>
    </div>
</template>
